import { create } from 'zustand';
import api from '../lib/api';
import type { AuditChange } from '../types/admin';

export interface TimelineFilters {
  source?: string;
  entityType?: string;
  fieldName?: string;
  from?: string;
  to?: string;
}

interface AuditState {
  timeline: AuditChange[];
  timelineCursor: string | null;
  timelineLoading: boolean;
  timelineError: string | null;
  timelineFilters: TimelineFilters;

  fetchTimeline: (filters?: TimelineFilters) => Promise<void>;
  loadMoreTimeline: () => Promise<void>;
  setTimelineFilters: (f: TimelineFilters) => void;
}

export const useAuditStore = create<AuditState>((set, get) => ({
  timeline: [],
  timelineCursor: null,
  timelineLoading: false,
  timelineError: null,
  timelineFilters: {},

  async fetchTimeline(filters) {
    const f = filters ?? get().timelineFilters;
    set({ timelineLoading: true, timelineFilters: f });
    try {
      const res = await api.get<{ changes: AuditChange[]; nextCursor: string | null }>(
        '/api/audit/timeline',
        { params: { ...f, limit: 50 } },
      );
      set({
        timeline: res.data.changes,
        timelineCursor: res.data.nextCursor,
        timelineLoading: false,
        timelineError: null,
      });
    } catch (err: unknown) {
      set({
        timelineLoading: false,
        timelineError: err instanceof Error ? err.message : 'Failed to load',
      });
    }
  },

  async loadMoreTimeline() {
    const cursor = get().timelineCursor;
    if (!cursor) return;
    set({ timelineLoading: true });
    try {
      const res = await api.get<{ changes: AuditChange[]; nextCursor: string | null }>(
        '/api/audit/timeline',
        { params: { ...get().timelineFilters, limit: 50, before: cursor } },
      );
      set({
        timeline: [...get().timeline, ...res.data.changes],
        timelineCursor: res.data.nextCursor,
        timelineLoading: false,
      });
    } catch (err: unknown) {
      set({
        timelineLoading: false,
        timelineError: err instanceof Error ? err.message : 'Failed to load',
      });
    }
  },

  setTimelineFilters(f) {
    set({ timelineFilters: f });
  },
}));
